import React, { useCallback, useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Mode Présentation plein écran pour les fiches "Session de circulation".
 * - Slides : Couverture / Phrase clé / Objectifs / Pré-requis / Déroulé (1 slide par étape)
 *           / Contenus / Évaluation (1 slide par QCM) / Cas pratique / Conseils jury / Fin
 * - Navigation : clic, flèches gauche/droite, touche Espace/PageDown (suivant), PageUp (précédent), Échap (fermer)
 */
export default function PresentationMode({ cours, open, onClose }) {
  const [index, setIndex] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const accent = cours?.accent || '#cf2031';
  const Icon = (cours && Icons[cours.icon]) || Icons.BookOpen;

  // Construction des slides à partir des données de la fiche
  const slides = React.useMemo(() => {
    if (!cours) return [];
    const list = [];

    // 1. Couverture
    list.push({
      kind: 'cover',
      title: cours.title,
      subtitle: cours.subtitle,
      meta: `${cours.level} · ${cours.duration} · ${cours.format}`,
      phrase: cours.phraseCle,
    });

    // 2. Objectifs
    list.push({
      kind: 'objectifs',
      title: 'Objectifs opérationnels',
      blocks: [
        { label: 'Savoir', value: cours.objectifs?.savoir, color: '#3b82f6' },
        { label: 'Savoir-faire', value: cours.objectifs?.savoirFaire, color: '#f59e0b' },
        { label: 'Savoir-être', value: cours.objectifs?.savoirEtre, color: '#10b981' },
      ].filter((b) => !!b.value),
    });

    // 3. Pré-requis
    if (cours.prerequis?.length) {
      list.push({
        kind: 'list',
        title: 'Pré-requis élève',
        items: cours.prerequis,
      });
    }

    // 4. Déroulé : 1 slide par étape (max)
    cours.deroule?.forEach((d) => {
      list.push({
        kind: 'etape',
        title: d.titre,
        meta: `${d.duree} · ${d.role}`,
        animation: d.animation,
        questions: d.questions_eleve || [],
        attendu: d.attendu,
      });
    });

    // 5. Contenus pédagogiques
    cours.contenus?.forEach((c) => {
      list.push({
        kind: 'contenu',
        title: c.titre,
        meta: c.page_source,
        items: c.points_cles,
        schema: c.schema,
      });
    });

    // 6. Évaluation : 1 slide par QCM
    cours.evaluation?.questions?.forEach((q, i) => {
      list.push({
        kind: 'qcm',
        title: `Évaluation · Q${i + 1}`,
        question: q.q,
        choix: q.choix,
        bonne: q.bonne,
        explication: q.explication,
      });
    });

    // 7. Cas pratique
    if (cours.evaluation?.cas_pratique) {
      list.push({
        kind: 'cas',
        title: 'Cas pratique oral',
        enonce: cours.evaluation.cas_pratique.enonce,
        reponse: cours.evaluation.cas_pratique.reponse_attendue,
      });
    }

    // 8. Conseils jury
    if (cours.conseils_jury?.length) {
      list.push({
        kind: 'list',
        title: 'Conseils jury CCS',
        items: cours.conseils_jury,
      });
    }

    // 9. Fin
    list.push({
      kind: 'fin',
      title: 'Fin de la séance',
      subtitle: 'Merci pour votre attention',
      source: cours.source,
    });

    return list;
  }, [cours]);

  const total = slides.length;

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  // Reset à l'ouverture
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  // Raccourcis clavier
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Home') {
        e.preventDefault();
        setIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setIndex(total - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, next, prev, onClose, total]);

  // Bloquer scroll body
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const toggleFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFs(true)).catch((e) => {
        console.warn('Fullscreen failed', e);
      });
    } else {
      document.exitFullscreen?.().then(() => setIsFs(false)).catch((e) => {
        console.warn('Exit fullscreen failed', e);
      });
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  if (!open || !cours) return null;

  const slide = slides[index];
  const progressPct = total > 1 ? ((index) / (total - 1)) * 100 : 100;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#0b0d10] text-white flex flex-col"
      data-testid="presentation-mode"
      role="dialog"
      aria-label={`Mode présentation : ${cours.title}`}
    >
      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-4 sm:px-8 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: accent }}
          >
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-black">
              Mode présentation · CCS ECSR
            </div>
            <div className="text-sm font-bold truncate">{cours.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/60 hidden sm:inline tabular-nums">
            {index + 1} / {total}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/10"
            data-testid="presentation-fullscreen"
            aria-label={isFs ? 'Quitter le plein écran' : 'Plein écran'}
          >
            {isFs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="text-white hover:bg-white/10"
            data-testid="presentation-close"
            aria-label="Fermer la présentation"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Barre de progression */}
      <div className="h-1 bg-white/10 flex-shrink-0">
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${progressPct}%`, background: accent }}
        />
      </div>

      {/* Contenu de la slide */}
      <main className="flex-1 overflow-y-auto px-4 sm:px-12 py-6 sm:py-10">
        <div className="max-w-5xl mx-auto w-full">
          <SlideRenderer slide={slide} accent={accent} />
        </div>
      </main>

      {/* Footer / navigation */}
      <footer className="flex items-center justify-between gap-3 px-4 sm:px-8 py-3 border-t border-white/10 flex-shrink-0">
        <Button
          variant="outline"
          onClick={prev}
          disabled={index === 0}
          className="bg-white/5 border-white/20 text-white hover:bg-white/10 disabled:opacity-30"
          data-testid="presentation-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
        </Button>
        <div className="text-xs text-white/50 hidden sm:flex items-center gap-3 tabular-nums">
          <span>← →</span>
          <span>·</span>
          <span>Échap pour quitter</span>
        </div>
        <Button
          onClick={next}
          disabled={index === total - 1}
          className="text-white disabled:opacity-30"
          style={{ background: accent }}
          data-testid="presentation-next"
        >
          Suivant <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </footer>
    </div>
  );
}

function SlideRenderer({ slide, accent }) {
  if (!slide) return null;

  switch (slide.kind) {
    case 'cover':
      return (
        <div className="text-center py-8 sm:py-16">
          <div
            className="inline-block text-[11px] font-black uppercase tracking-[0.3em] px-3 py-1 rounded-full mb-6"
            style={{ background: `${accent}22`, color: accent }}
          >
            Fiche séance · {slide.meta}
          </div>
          <h1 className="text-3xl sm:text-6xl font-black leading-[1.05] tracking-tight mb-6">
            {slide.title}
          </h1>
          {slide.subtitle && (
            <p className="text-base sm:text-2xl text-white/70 mb-8 max-w-3xl mx-auto">
              {slide.subtitle}
            </p>
          )}
          {slide.phrase && (
            <blockquote
              className="text-lg sm:text-3xl italic font-light max-w-3xl mx-auto border-l-4 pl-4 sm:pl-6 text-left"
              style={{ borderColor: accent }}
            >
              « {slide.phrase} »
            </blockquote>
          )}
        </div>
      );

    case 'objectifs':
      return (
        <div>
          <SlideTitle accent={accent}>{slide.title}</SlideTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {slide.blocks.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 sm:p-6 border border-white/10"
                style={{ background: `${b.color}15` }}
              >
                <div
                  className="text-[11px] font-black uppercase tracking-widest mb-2"
                  style={{ color: b.color }}
                >
                  {b.label}
                </div>
                <p className="text-base sm:text-lg leading-relaxed text-white/90">{b.value}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'list':
      return (
        <div>
          <SlideTitle accent={accent}>{slide.title}</SlideTitle>
          <ul className="space-y-3 sm:space-y-4 mt-8">
            {slide.items.map((it, i) => (
              <li key={i} className="flex gap-3 text-base sm:text-xl leading-relaxed">
                <span className="font-black flex-shrink-0" style={{ color: accent }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-white/90">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'etape':
      return (
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-white/50 mb-2">
            Déroulé · {slide.meta}
          </div>
          <SlideTitle accent={accent}>{slide.title}</SlideTitle>
          <p className="text-base sm:text-xl text-white/85 mt-6 leading-relaxed">{slide.animation}</p>
          {slide.questions?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6">
              <div
                className="text-[11px] font-black uppercase tracking-widest mb-3"
                style={{ color: accent }}
              >
                Questions à poser à l'élève
              </div>
              <ul className="space-y-2 sm:space-y-3">
                {slide.questions.map((q, i) => (
                  <li key={i} className="text-sm sm:text-lg text-white/90 italic">
                    « {q} »
                  </li>
                ))}
              </ul>
            </div>
          )}
          {slide.attendu && (
            <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 sm:p-6">
              <div className="text-[11px] font-black uppercase tracking-widest mb-2 text-emerald-300">
                Réponse cible
              </div>
              <p className="text-sm sm:text-lg text-white/90">{slide.attendu}</p>
            </div>
          )}
        </div>
      );

    case 'contenu':
      return (
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-white/50 mb-2">
            Contenu pédagogique · {slide.meta}
          </div>
          <SlideTitle accent={accent}>{slide.title}</SlideTitle>
          <ul className="space-y-3 mt-8">
            {slide.items.map((it, i) => (
              <li key={i} className="flex gap-3 text-base sm:text-xl leading-relaxed">
                <span style={{ color: accent }} className="font-black flex-shrink-0">●</span>
                <span className="text-white/90">{it}</span>
              </li>
            ))}
          </ul>
          {slide.schema && (
            <div
              className="mt-6 italic text-sm sm:text-base text-white/70 border-l-4 pl-4"
              style={{ borderColor: accent }}
            >
              <strong className="not-italic text-white/90">Support visuel :</strong> {slide.schema}
            </div>
          )}
        </div>
      );

    case 'qcm':
      return (
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-white/50 mb-2">
            {slide.title}
          </div>
          <h2 className="text-2xl sm:text-4xl font-black leading-tight mb-8">{slide.question}</h2>
          <ol className="space-y-3 list-[upper-alpha] pl-6">
            {slide.choix.map((ch, i) => (
              <li
                key={i}
                className={`text-base sm:text-xl pl-2 ${
                  i === slide.bonne
                    ? 'text-emerald-300 font-black'
                    : 'text-white/80'
                }`}
              >
                {ch}{i === slide.bonne ? '  ✓' : ''}
              </li>
            ))}
          </ol>
          <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 sm:p-6">
            <div className="text-[11px] font-black uppercase tracking-widest mb-2 text-emerald-300">
              Explication
            </div>
            <p className="text-sm sm:text-lg text-white/90 leading-relaxed">{slide.explication}</p>
          </div>
        </div>
      );

    case 'cas':
      return (
        <div>
          <SlideTitle accent={accent}>{slide.title}</SlideTitle>
          <blockquote
            className="text-lg sm:text-2xl italic mt-8 border-l-4 pl-4 sm:pl-6 text-white/90"
            style={{ borderColor: accent }}
          >
            « {slide.enonce} »
          </blockquote>
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-6">
            <div
              className="text-[11px] font-black uppercase tracking-widest mb-2"
              style={{ color: accent }}
            >
              Réponse attendue
            </div>
            <p className="text-sm sm:text-lg text-white/90 leading-relaxed">{slide.reponse}</p>
          </div>
        </div>
      );

    case 'fin':
      return (
        <div className="text-center py-12 sm:py-24">
          <h1 className="text-4xl sm:text-7xl font-black mb-6" style={{ color: accent }}>
            {slide.title}
          </h1>
          {slide.subtitle && (
            <p className="text-lg sm:text-2xl text-white/70 mb-12">{slide.subtitle}</p>
          )}
          {slide.source && (
            <div className="text-xs sm:text-sm text-white/40">
              Source : {slide.source.label} ({slide.source.pages})
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
}

function SlideTitle({ children, accent }) {
  return (
    <h2
      className="text-2xl sm:text-5xl font-black leading-tight border-l-4 pl-4 sm:pl-6"
      style={{ borderColor: accent }}
    >
      {children}
    </h2>
  );
}
